import SwitchWithLabel from 'components/SwitchWithLabel/SwitchWithLabel';
import React, { useEffect, useMemo, useState } from 'react';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { contractGenI18n } from '../i18n';
import { createFeeIncreaseRequest } from '../../../services/contractGeneratorService';

const StepCosts = ({ DesignSystemComponents, formData, onChange }) => {
  const { Input, Button, Modal, Table, Toast } = DesignSystemComponents;
  const { t } = useScopedTranslation(contractGenI18n);

  const costos = formData?.costos || {};
  const hasFee = Boolean(costos.hasFee);
  const hasDownPayment = Boolean(costos.hasDownPayment);
  const [modalMoreFees, setModalMoreFees] = useState(false);
  const [requestedFees, setRequestedFees] = useState('');
  const [requestedReason, setRequestedReason] = useState('');

  const numberOfFees = Number(costos.numeroCuotas || 0);
  const totalAmount = Number(costos.costoTotal || 0);

  const downPaymentAmount = hasDownPayment ? Number(costos.montoPie || 0) : 0;
  const amountForFees = Math.max(0, totalAmount - downPaymentAmount);

  const feeRows = useMemo(() => {
    if (!hasFee || numberOfFees <= 0 || amountForFees <= 0) return [];
    const base = Math.floor((amountForFees / numberOfFees) * 100) / 100;
    const rows = [];
    for (let i = 1; i <= numberOfFees; i++) {
      rows.push({
        fee: String(i),
        day: costos.diaPago || '',
        amount: i === numberOfFees ? (amountForFees - base * (numberOfFees - 1)).toFixed(2) : base.toFixed(2),
      });
    }
    return rows;
  }, [hasFee, numberOfFees, amountForFees, costos.diaPago]);

  // Persist computed fee amount into formData so handleFinish can use it
  useEffect(() => {
    if (hasFee && numberOfFees > 0 && totalAmount > 0) {
      const downPayment = hasDownPayment ? Number(costos.montoPie || 0) : 0;
      const amountAfterPie = Math.max(0, totalAmount - downPayment);
      const perFee = Math.floor((amountAfterPie / numberOfFees) * 100) / 100;
      if (perFee !== Number(costos.valorPorCuota || 0)) {
        onChange({ costos: { ...costos, valorPorCuota: perFee } });
      }
    }
  }, [hasFee, numberOfFees, totalAmount, hasDownPayment, costos.montoPie]);

  const requestMoreFees = async () => {
    try {
      if (!formData?.contractId && !formData?.id) {
        setModalMoreFees(false);
        return;
      }
      await createFeeIncreaseRequest(formData.contractId || formData.id, {
        requestedFees: Number(requestedFees || 0),
        reason: requestedReason || null,
      });
      setModalMoreFees(false);
      setRequestedFees('');
      setRequestedReason('');
      if (Toast?.show) {
        Toast.show({
          title: t('cg_costs_toast_request_sent'),
          message: t('cg_costs_toast_request_sent_msg'),
          variant: 'success',
        });
      }
    } catch (error) {
      console.error(error);
      if (Toast?.show) {
        Toast.show({
          title: t('cg_costs_toast_error'),
          message:
            error?.customMessage ||
            error?.message ||
            t('cg_costs_toast_request_error'),
          variant: 'error',
        });
      }
    }
  };

  return (
    <div className='step-content'>
      <div className='d-flex align-items-center justify-content-between mb-2'>
        <p className='body fw-semibold'>{t('cg_costs_title')}</p>
        {hasFee && (
          <Button
            label={t('cg_costs_request_more_fees')}
            variant='naked'
            iconStart='plus'
            onClick={() => setModalMoreFees(true)}
          />
        )}
      </div>

      <div className='row'>
        <div className='col-6'>
          <div className='row mb-3'>
            <div className='col-6'>
              <SwitchWithLabel
                variant='field'
                label={t('cg_costs_switch_fees')}
                checked={hasFee}
                onChange={(checked) =>
                  onChange({ costos: { ...costos, hasFee: checked } })
                }
                helperText={t('cg_costs_fees_helper')}
              />
            </div>
            <div className='col-6'>
              {!hasFee ? (
                <Input
                  label={t('cg_costs_payment_date')}
                  type='date'
                  value={costos.fechaPago || ''}
                  min=''
                  onChange={(e) =>
                    onChange({
                      costos: { ...costos, fechaPago: e.target.value },
                    })
                  }
                  required
                />
              ) : (
                <SwitchWithLabel
                  variant='field'
                  label={t('cg_costs_switch_down_payment')}
                  checked={hasDownPayment}
                  onChange={(checked) =>
                    onChange({ costos: { ...costos, hasDownPayment: checked } })
                  }
                  helperText={t('cg_costs_down_payment_helper')}
                />
              )}
            </div>
          </div>

          <div className='row mb-3'>
            <div className='col-12'>
              <Input
                label={t('cg_costs_total')}
                name='total-amount'
                onChange={(e) =>
                  onChange({
                    costos: { ...costos, costoTotal: e.target.value },
                  })
                }
                placeholder={t('cg_costs_total_placeholder')}
                type='number'
                value={costos.costoTotal || ''}
                required
              />
            </div>
          </div>

          {hasFee && (
            <>
              <div className='row mb-3'>
                <div className='col-6'>
                  <Input
                    label={t('cg_costs_number_fees')}
                    name='number-fees'
                    onChange={(e) =>
                      onChange({
                        costos: { ...costos, numeroCuotas: e.target.value },
                      })
                    }
                    placeholder={t('cg_costs_number_fees_placeholder')}
                    type='number'
                    value={costos.numeroCuotas || ''}
                    required
                  />
                </div>
                <div className='col-6'>
                  <Input
                    label={t('cg_costs_pay_day')}
                    name='pay-day'
                    onChange={(e) =>
                      onChange({
                        costos: { ...costos, diaPago: e.target.value },
                      })
                    }
                    placeholder={t('cg_costs_pay_day_placeholder')}
                    type='number'
                    value={costos.diaPago || ''}
                    required
                  />
                </div>
              </div>
              {hasDownPayment && (
                <div className='row mb-3'>
                  <div className='col-6'>
                    <Input
                      label={t('cg_costs_down_payment_amount')}
                      name='down-payment'
                      onChange={(e) =>
                        onChange({
                          costos: { ...costos, montoPie: e.target.value },
                        })
                      }
                      placeholder={t('cg_costs_down_payment_placeholder')}
                      type='number'
                      value={costos.montoPie || ''}
                      required
                    />
                  </div>
                  <div className='col-6 d-flex align-items-end'>
                    <p className='body color-gray-600 mb-2'>
                      {t('cg_costs_fees_calculated_on', { amount: new Intl.NumberFormat().format(Math.max(0, totalAmount - Number(costos.montoPie || 0))) })}
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className='col-6'>
          {hasFee && feeRows.length > 0 && (
            <div className='table-container table-fees'>
              <Table
                columns={[
                  { accessor: 'fee', label: t('cg_costs_table_fee') },
                  { accessor: 'day', label: t('cg_costs_table_day') },
                  { accessor: 'amount', label: t('cg_costs_table_amount') },
                ]}
                data={feeRows}
              />
            </div>
          )}
        </div>
      </div>

      <Modal
        onClose={() => setModalMoreFees(false)}
        show={modalMoreFees}
        primaryBtnAction={requestMoreFees}
        primaryBtnTxt={t('cg_costs_modal_btn_request')}
        secondaryBtnAction={() => setModalMoreFees(false)}
        secondaryBtnTxt={t('cg_costs_modal_btn_cancel')}
        title={t('cg_costs_modal_title')}
        width='600px'
      >
        <div className='row'>
          <div className='col-6'>
            <Input
              label={t('cg_costs_modal_number_fees')}
              name='requested-fees'
              onChange={(e) => setRequestedFees(e.target.value)}
              placeholder={t('cg_costs_modal_fees_placeholder')}
              type='number'
              value={requestedFees}
              required
            />
          </div>
          <div className='col-6'>
            <Input
              label={t('cg_costs_modal_reason')}
              name='request-reason'
              onChange={(e) => setRequestedReason(e.target.value)}
              placeholder={t('cg_costs_modal_reason_placeholder')}
              type='text'
              value={requestedReason}
            />
          </div>
        </div>
        <p className='body'>{t('cg_costs_modal_info')}</p>
      </Modal>
    </div>
  );
};

export default StepCosts;
